"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useState, useEffect, useMemo, useSyncExternalStore } from "react"
import { B2B_CADASTRO_HREF, isB2BLandingPath } from "@/lib/landing/b2b-landing-paths"
import { buildPartnerCadastroHref } from "@/lib/partners/build-agent-share-url"
import { readPartnerAgentCodeFromSearchParams } from "@/lib/partners/read-agent-param"
import { getPartnerSlugFromPathname } from "@/lib/partners/partner-path"
import { Button } from "@/components/ui/button"
import { Menu, X } from "lucide-react"
import { AstraLogo, BRAND_FLOW_LOGO_SIZE } from "@/components/astra-logo"
import { cn } from "@/lib/utils"
import {
  HEADER_NAV_ITEMS,
  hasAlwaysDarkLogo,
  hasTransparentHeader,
  resolveNavItemHref,
} from "@/lib/site-navigation"

function subscribePartnerAgentSearch() {
  return () => {}
}

function readPartnerAgentCodeFromWindow(): string | null {
  try {
    return readPartnerAgentCodeFromSearchParams(new URLSearchParams(window.location.search))
  } catch {
    return null
  }
}

export function Header() {
  const pathname = usePathname()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [loggedIn, setLoggedIn] = useState(false)
  const partnerAgentCode = useSyncExternalStore(
    subscribePartnerAgentSearch,
    readPartnerAgentCodeFromWindow,
    () => null,
  )

  const cadastroHref = useMemo(() => {
    if (isB2BLandingPath(pathname)) return B2B_CADASTRO_HREF
    const partnerSlug = getPartnerSlugFromPathname(pathname)
    if (!partnerSlug) return "/cadastro"
    return buildPartnerCadastroHref(partnerSlug, partnerAgentCode)
  }, [pathname, partnerAgentCode])

  useEffect(() => {
    let cancelled = false
    const w = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback) => number
      cancelIdleCallback?: (handle: number) => void
    }

    const loadSessionState = async () => {
      const { createClient } = await import("@/lib/supabase/client")
      const supabase = createClient()
      const { data } = await supabase.auth.getSession()
      if (!cancelled) setLoggedIn(!!data.session)
    }

    // Defer auth check so initial render remains focused on above-the-fold content.
    if (typeof w.requestIdleCallback === "function") {
      const idleId = w.requestIdleCallback(() => {
        void loadSessionState()
      })
      return () => {
        cancelled = true
        if (typeof w.cancelIdleCallback === "function") {
          w.cancelIdleCallback(idleId)
        }
      }
    }

    const timeoutId = globalThis.setTimeout(() => {
      void loadSessionState()
    }, 600)

    return () => {
      cancelled = true
      globalThis.clearTimeout(timeoutId)
    }
  }, [])

  const usesTransparentHeader = hasTransparentHeader(pathname)
  const isSolidHeader = !usesTransparentHeader || scrolled
  const logoVariant = hasAlwaysDarkLogo(pathname) || isSolidHeader ? "dark" : "light"

  useEffect(() => {
    const handleScroll = () => {
      if (!usesTransparentHeader) {
        setScrolled(true)
        return
      }
      const heroElement = document.getElementById("hero")
      const heroHeight = heroElement ? heroElement.offsetHeight - 80 : window.innerHeight - 80
      setScrolled(window.scrollY > heroHeight)
    }
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [usesTransparentHeader, pathname])

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md",
        isSolidHeader
          ? "bg-white/95 shadow-sm border-b border-border"
          : "bg-brand-dark/30"
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link href="/" className="flex-shrink-0">
            <AstraLogo size={BRAND_FLOW_LOGO_SIZE} variant={logoVariant} />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {HEADER_NAV_ITEMS.map((item) => (
              <Link
                key={item.name}
                href={resolveNavItemHref(pathname, item)}
                className={cn(
                  "text-sm font-medium transition-all duration-200 relative",
                  isSolidHeader
                    ? "text-muted-foreground hover:text-primary"
                    : "text-white/80 hover:text-white",
                  "after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-current after:transition-all after:duration-200 hover:after:w-full"
                )}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {loggedIn ? (
              <Button
                size="sm"
                asChild
                className={cn(
                  "bg-[#36B2FF] hover:bg-[#2a9be6]",
                  usesTransparentHeader && !scrolled && "shadow-sm",
                )}
              >
                <Link href="/painel">Minha conta</Link>
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className={cn(
                    isSolidHeader ? "" : "text-white hover:text-white hover:bg-white/10",
                  )}
                >
                  <Link href="/entrar">Entrar</Link>
                </Button>
                <Button size="sm" asChild className="bg-[#36B2FF] hover:bg-[#2a9be6]">
                  <Link href={cadastroHref}>Começar agora</Link>
                </Button>
              </>
            )}
          </div>

          <button
            className={cn(
              "lg:hidden p-2 rounded-md transition-colors",
              isSolidHeader
                ? "text-foreground hover:bg-secondary"
                : "text-white hover:bg-white/10"
            )}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {isMenuOpen && (
          <div className={cn(
            "lg:hidden py-4 border-t",
            isSolidHeader ? "border-border" : "border-white/20"
          )}>
            <nav className="flex flex-col gap-1">
              {HEADER_NAV_ITEMS.map((item) => (
                <Link
                  key={item.name}
                  href={resolveNavItemHref(pathname, item)}
                  className={cn(
                    "py-2 px-3 rounded-md text-sm font-medium transition-colors",
                    isSolidHeader
                      ? "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div
                className={cn(
                  "flex flex-col gap-3 pt-6 mt-4 border-t",
                  isSolidHeader ? "border-border" : "border-white/20",
                )}
              >
                {loggedIn ? (
                  <Button size="lg" asChild className="w-full h-12 text-base bg-[#36B2FF] hover:bg-[#2a9be6]">
                    <Link href="/painel" onClick={() => setIsMenuOpen(false)}>
                      Minha conta
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="lg"
                      asChild
                      className={cn(
                        "w-full h-12 text-base font-medium",
                        isSolidHeader
                          ? "border-border text-foreground hover:bg-secondary"
                          : "border-white/60 bg-white/10 text-white hover:bg-white/20 hover:text-white",
                      )}
                    >
                      <Link href="/entrar" onClick={() => setIsMenuOpen(false)}>
                        Entrar
                      </Link>
                    </Button>
                    <Button size="lg" asChild className="w-full h-12 text-base bg-[#36B2FF] hover:bg-[#2a9be6]">
                      <Link href={cadastroHref} onClick={() => setIsMenuOpen(false)}>
                        Começar agora
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
